/**
 * Step two: the code.
 *
 * Every way this can fail produces one message, set by the caller. The widget
 * is reset after each attempt so a retry has a fresh challenge to send.
 */

import { useState, type FormEvent } from 'react';

import { useTurnstile } from '../hooks/useTurnstile';
import { Notice } from './Notice';
import {
  CODE_BACK,
  CODE_HEADING,
  CODE_LABEL,
  CODE_SUBMIT,
  TURNSTILE_FAILED_MESSAGE,
} from '../copy';

interface CodeStepProps {
  email: string;
  notice: string | null;
  busy: boolean;
  onSubmit: (code: string, turnstileToken: string) => Promise<void>;
  onBack: () => void;
}

export function CodeStep({ email, notice, busy, onSubmit, onBack }: CodeStepProps) {
  const [code, setCode] = useState('');
  const turnstile = useTurnstile('portal-verify');

  const canSubmit = !busy && turnstile.token !== null && code.trim().length > 0;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || turnstile.token === null) return;
    const token = turnstile.token;
    turnstile.reset();
    setCode('');
    await onSubmit(code.trim(), token);
  }

  return (
    <section className="panel">
      <h1>{CODE_HEADING}</h1>
      <p className="lede gate-email">{email}</p>
      <Notice message={notice} />
      <form onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field-label">{CODE_LABEL}</span>
          <input
            type="text"
            name="code"
            autoComplete="one-time-code"
            inputMode="numeric"
            autoCapitalize="off"
            spellCheck={false}
            required
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </label>
        <div className="turnstile" ref={turnstile.containerRef} />
        {turnstile.failed ? <Notice message={TURNSTILE_FAILED_MESSAGE} /> : null}
        <button type="submit" className="button-primary" disabled={!canSubmit}>
          {CODE_SUBMIT}
        </button>
      </form>
      <button type="button" className="button-link" onClick={onBack} disabled={busy}>
        {CODE_BACK}
      </button>
    </section>
  );
}
